import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AuthContext } from '../context/AuthContext';
import { SERVER_URL, WS_URL } from '../config';
import io from 'socket.io-client';
import { format, formatDistance } from 'date-fns';
import { FaFileDownload, FaFileAlt, FaVideo } from 'react-icons/fa'; // Add FaVideo icon

const MeetingHistory = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [transcription, setTranscription] = useState(null);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  
  // Add state for recordings
  const [recordings, setRecordings] = useState([]);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [loadingRecording, setLoadingRecording] = useState(false);
  
  // Add new state variables for calendar meetings
  const [calendarMeetings, setCalendarMeetings] = useState([]);
  const [attendedMeetings, setAttendedMeetings] = useState([]);
  const [missedMeetings, setMissedMeetings] = useState([]);
  const [displayMode, setDisplayMode] = useState('created'); // 'created', 'attended', 'missed'

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(WS_URL, {
      transports: ['websocket'],
      withCredentials: true
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Listen for real-time updates
  useEffect(() => {
    if (!socket) return;

    // Listen for new comments
    socket.on('newComment', (comment) => {
      setMeetings(prevMeetings => {
        return prevMeetings.map(meeting => {
          if (meeting.streamId === comment.streamId) {
            // Add the comment to the meeting
            const updatedMeeting = { ...meeting };
            if (!updatedMeeting.comments) {
              updatedMeeting.comments = [];
            }
            updatedMeeting.comments.push(comment);
            return updatedMeeting;
          }
          return meeting;
        });
      });
    });

    // Listen for new likes
    socket.on('newLike', ({ streamId, likes }) => {
      setMeetings(prevMeetings => {
        return prevMeetings.map(meeting => {
          if (meeting.streamId === streamId) {
            return { ...meeting, likes };
          }
          return meeting;
        });
      });
    });

    // Listen for status updates
    socket.on('streamStatusUpdate', ({ streamId, status }) => {
      setMeetings(prevMeetings => {
        return prevMeetings.map(meeting => {
          if (meeting.streamId === streamId) {
            return { ...meeting, status };
          }
          return meeting;
        });
      });
    });

    return () => {
      socket.off('newComment');
      socket.off('newLike');
      socket.off('streamStatusUpdate');
    };
  }, [socket]);

  // Fetch meeting history and calendar meetings
  useEffect(() => {
    if (!user) return;

    const fetchMeetingHistory = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${SERVER_URL}/streams/history/${user.username}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch meeting history');
        }

        const data = await response.json();
        setMeetings(data.streams);
        setFilteredMeetings(data.streams);
        setStatusCounts(data.statusCounts);
      } catch (error) {
        console.error('Error fetching meeting history:', error);
      }
    };

    const fetchCalendarMeetings = async () => {
      try {
        const response = await fetch(`${SERVER_URL}/scheduled-meetings/user/${user.username}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch calendar meetings');
        }
        
        const data = await response.json();
        
        // Process calendar meetings
        const now = new Date();
        const attended = [];
        const missed = [];
        
        // Fetch streams to check which meetings the user has joined
        const streamsResponse = await fetch(`${SERVER_URL}/streams/history/${user.username}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!streamsResponse.ok) {
          throw new Error('Failed to fetch streams history');
        }
        
        const streamsData = await streamsResponse.json();
        const joinedStreamIds = streamsData.streams.map(stream => stream.streamId);
        
        data.meetings.forEach(meeting => {
          const meetingEnd = new Date(new Date(meeting.scheduledTime).getTime() + meeting.duration * 60000);
          const isParticipant = meeting.participants.some(p => 
            p.username === user.username || p.email === user.email
          );
          const isHost = meeting.hostUsername === user.username;
          
          // Check if this meeting has a corresponding stream that the user joined
          const meetingStreamId = meeting.meetingId; // Assuming meetingId is used as streamId
          const hasJoined = joinedStreamIds.includes(meetingStreamId);
          
          // For missed meetings (past meetings that user was invited to but didn't join)
          if (isParticipant && !isHost && meetingEnd < now && !hasJoined) {
            missed.push({
              ...meeting,
              end: meetingEnd,
              type: 'calendar'
            });
          }
        });
        
        setCalendarMeetings(data.meetings);
        setAttendedMeetings(attended);
        setMissedMeetings(missed);
      } catch (error) {
        console.error('Error fetching calendar meetings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingHistory();
    fetchCalendarMeetings();

    // Set up interval to refresh data every minute
    const intervalId = setInterval(() => {
      fetchMeetingHistory();
      fetchCalendarMeetings();
    }, 60000);

    return () => clearInterval(intervalId);
  }, [user]);

  // Update filtering logic to handle different display modes
  useEffect(() => {
    if (displayMode === 'created') {
      // Original filtering logic for created meetings
      if (activeFilter === 'all') {
        setFilteredMeetings(meetings);
      } else {
        setFilteredMeetings(meetings.filter(meeting => meeting.status === activeFilter));
      }
    } else if (displayMode === 'attended') {
      setFilteredMeetings(attendedMeetings);
    } else if (displayMode === 'missed') {
      setFilteredMeetings(missedMeetings);
    }
  }, [activeFilter, meetings, displayMode, attendedMeetings, missedMeetings]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleDisplayModeChange = (mode) => {
    setDisplayMode(mode);
  };

  const joinMeeting = (streamId) => {
    navigate(`/view/${streamId}`);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    let result = '';
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0 || hours > 0) result += `${minutes}m `;
    result += `${remainingSeconds}s`;
    
    return result;
  };

  // Add a function to fetch transcription
  const fetchTranscription = async (streamId) => {
    try {
      setLoadingTranscript(true);
      const response = await fetch(`${SERVER_URL}/transcriptions/stream/${streamId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // No transcription found is a normal case
          setTranscription(null);
          return;
        }
        throw new Error('Failed to fetch transcription');
      }
      
      const data = await response.json();
      setTranscription(data);
    } catch (error) {
      console.error('Error fetching transcription:', error);
      setTranscription(null);
    } finally {
      setLoadingTranscript(false);
    }
  };

  // Function to handle downloading transcription
  const handleDownloadTranscription = async (meetingId, format = 'txt') => {
    try {
      // Create a direct link to download the file
      const token = localStorage.getItem('token');
      const downloadUrl = `${SERVER_URL}/transcription/download/${meetingId}?format=${format}&token=${token}`;
      
      // Open the URL in a new tab/window
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading transcription:', error);
      alert('Failed to download transcription');
    }
  };

  // Function to view transcription
  const viewTranscription = async (meeting) => {
    setSelectedMeeting(meeting);
    await fetchTranscription(meeting.streamId);
    setShowTranscriptModal(true);
  };


  // Function to fetch recordings for a stream
  const fetchRecordings = async (streamId) => {
    try {
      setLoadingRecording(true);
      const response = await fetch(`${SERVER_URL}/streams/${streamId}/recordings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // No recordings found is a normal case
          setRecordings([]);
          return;
        }
        throw new Error('Failed to fetch recordings');
      }
      
      const data = await response.json();
      setRecordings(data);
    } catch (error) {
      console.error('Error fetching recordings:', error);
      setRecordings([]);
    } finally {
      setLoadingRecording(false);
    }
  };

  // Function to view recording
  const viewRecording = async (meeting) => {
    setSelectedMeeting(meeting);
    await fetchRecordings(meeting.streamId);
    setShowRecordingModal(true);
  };

  // Function to handle viewing recording (wrapper for viewRecording)
  const handleViewRecording = (meeting) => {
    viewRecording(meeting);
  };

  const handleViewTranscript = (meeting) => {
    viewTranscription(meeting);
  };

  if (loading) {
    return <LoadingContainer>Loading meeting history...</LoadingContainer>;
  }

  return (
    <Container>
      <Header>
        <h1>Meeting History</h1>
        <StatusCountsContainer>
          <StatusCount>
            <span>Total: {statusCounts?.total || 0}</span>
          </StatusCount>
          <StatusCount>
            <span>Connected: {statusCounts?.connected || 0}</span>
          </StatusCount>
          <StatusCount>
            <span>In Progress: {statusCounts?.in_progress || 0}</span>
          </StatusCount>
          <StatusCount>
            <span>Ongoing: {statusCounts?.ongoing || 0}</span>
          </StatusCount>
          <StatusCount>
            <span>Completed: {statusCounts?.completed || 0}</span>
          </StatusCount>
        </StatusCountsContainer>
      </Header>

      {/* Add display mode tabs */}
      <DisplayModeTabs>
        <DisplayModeTab 
          active={displayMode === 'created'} 
          onClick={() => handleDisplayModeChange('created')}
        >
          Created by Me
        </DisplayModeTab>
        <DisplayModeTab 
          active={displayMode === 'attended'} 
          onClick={() => handleDisplayModeChange('attended')}
        >
          Attended
        </DisplayModeTab>
        <DisplayModeTab 
          active={displayMode === 'missed'} 
          onClick={() => handleDisplayModeChange('missed')}
        >
          Missed
        </DisplayModeTab>
      </DisplayModeTabs>

      {/* Only show status filter tabs for created meetings */}
      {displayMode === 'created' && (
        <FilterTabs>
          <FilterTab 
            active={activeFilter === 'all'} 
            onClick={() => handleFilterChange('all')}
          >
            All
          </FilterTab>
          <FilterTab 
            active={activeFilter === 'connected'} 
            onClick={() => handleFilterChange('connected')}
          >
            Connected
          </FilterTab>
          <FilterTab 
            active={activeFilter === 'in_progress'} 
            onClick={() => handleFilterChange('in_progress')}
          >
            In Progress
          </FilterTab>
          <FilterTab 
            active={activeFilter === 'ongoing'} 
            onClick={() => handleFilterChange('ongoing')}
          >
            Ongoing
          </FilterTab>
          <FilterTab 
            active={activeFilter === 'completed'} 
            onClick={() => handleFilterChange('completed')}
          >
            Completed
          </FilterTab>
        </FilterTabs>
      )}

      {filteredMeetings.length === 0 ? (
        <NoMeetings>No meetings found.</NoMeetings>
      ) : (
        <MeetingsList>
          {filteredMeetings.map((meeting) => (
            <MeetingCard key={meeting.streamId || meeting.meetingId}>
              <MeetingHeader>
                <MeetingTitle>{meeting.title}</MeetingTitle>
                {meeting.type === 'calendar' ? (
                  <StatusBadge status={meeting.status || 'scheduled'}>
                    {meeting.status || 'Scheduled'}
                  </StatusBadge>
                ) : (
                  <StatusBadge status={meeting.status}>
                    {meeting.status}
                  </StatusBadge>
                )}
              </MeetingHeader>
              
              <MeetingDetails>
                <DetailItem>
                  <DetailLabel>Host:</DetailLabel>
                  <DetailValue>{meeting.hostUsername || 'N/A'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Started:</DetailLabel>
                  <DetailValue>
                    {meeting.startTime ? new Date(meeting.startTime).toLocaleString() : 'N/A'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Ended:</DetailLabel>
                  <DetailValue>
                    {meeting.endTime ? new Date(meeting.endTime).toLocaleString() : 'N/A'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Duration:</DetailLabel>
                  <DetailValue>
                    {meeting.duration ? formatDuration(meeting.duration) : 'N/A'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Participants:</DetailLabel>
                  <DetailValue>{meeting.participants?.length || 0}</DetailValue>
                </DetailItem>
              </MeetingDetails>
              
              <MeetingInteractions>
                <InteractionSection>
                  <InteractionHeader>
                    <h4>Comments</h4>
                  </InteractionHeader>
                  {meeting.comments && meeting.comments.length > 0 ? (
                    <CommentsList>
                      {meeting.comments.slice(0, 3).map((comment, index) => (
                        <CommentItem key={index}>
                          <CommentAuthor>{comment.username}</CommentAuthor>
                          <CommentText>{comment.text}</CommentText>
                          <CommentTime>
                            {new Date(comment.timestamp).toLocaleString()}
                          </CommentTime>
                        </CommentItem>
                      ))}
                      {meeting.comments.length > 3 && (
                        <MoreLikes>+{meeting.comments.length - 3} more comments</MoreLikes>
                      )}
                    </CommentsList>
                  ) : (
                    <NoInteractions>No comments yet</NoInteractions>
                  )}
                </InteractionSection>
                
                <InteractionSection>
                  <InteractionHeader>
                    <h4>Likes</h4>
                  </InteractionHeader>
                  {meeting.likes && meeting.likes.length > 0 ? (
                    <LikesList>
                      {meeting.likes.slice(0, 5).map((like, index) => (
                        <LikeItem key={index}>{like.username}</LikeItem>
                      ))}
                      {meeting.likes.length > 5 && (
                        <MoreLikes>+{meeting.likes.length - 5} more</MoreLikes>
                      )}
                    </LikesList>
                  ) : (
                    <NoInteractions>No likes yet</NoInteractions>
                  )}
                </InteractionSection>
              </MeetingInteractions>
              
              <MeetingActions>
                {meeting.status === 'ongoing' && (
                  <ActionButton onClick={() => joinMeeting(meeting.streamId)}>
                    Join Meeting
                  </ActionButton>
                )}
                <ActionButton 
                  secondary 
                  onClick={() => handleViewTranscript(meeting)}
                >
                  View Transcript
                </ActionButton>
                <ActionButton 
                  secondary 
                  onClick={() => handleViewRecording(meeting)}
                >
                  View Recording
                </ActionButton>
              </MeetingActions>
            </MeetingCard>
          ))}
        </MeetingsList>
      )}

      {/* Transcription Modal */}
      {showTranscriptModal && selectedMeeting && (
        <ModalOverlay>
          <TranscriptModal>
            <ModalHeader>
              <h3>Meeting Transcript: {selectedMeeting.title}</h3>
              <CloseButton onClick={() => setShowTranscriptModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            <ModalContent>
              {loadingTranscript ? (
                <LoadingContainer>Loading transcript...</LoadingContainer>
              ) : transcription && transcription.transcript && transcription.transcript.length > 0 ? (
                <>
                  <TranscriptActions>
                    <DownloadButton onClick={() => handleDownloadTranscription(transcription.meetingId)}>
                      <FaFileDownload style={{ marginRight: '5px' }} /> Download Transcript
                    </DownloadButton>
                  </TranscriptActions>
                  <TranscriptContainer>
                    {transcription.transcript.map((segment, index) => (
                      <TranscriptSegment key={index}>
                        <TranscriptTime>
                          {new Date(segment.timestamp).toLocaleTimeString()}
                        </TranscriptTime>
                        <TranscriptSpeaker>{segment.speakerUsername}:</TranscriptSpeaker>
                        <TranscriptText>{segment.text}</TranscriptText>
                      </TranscriptSegment>
                    ))}
                  </TranscriptContainer>
                </>
              ) : (
                <NoTranscript>
                  No transcript available for this meeting.
                </NoTranscript>
              )}
            </ModalContent>
          </TranscriptModal>
        </ModalOverlay>
      )}

      {/* Recording Modal */}
      {showRecordingModal && selectedMeeting && (
        <ModalOverlay>
          <TranscriptModal> {/* Reusing the same styled component */}
            <ModalHeader>
              <h3>Meeting Recordings: {selectedMeeting.title}</h3>
              <CloseButton onClick={() => setShowRecordingModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            <ModalContent>
              {loadingRecording ? (
                <LoadingContainer>Loading recordings...</LoadingContainer>
              ) : recordings && recordings.length > 0 ? (
                <>
                  <TranscriptContainer> {/* Reusing the same styled component */}
                    {recordings.map((recording, index) => (
                      <RecordingItem key={index}>
                        <RecordingTitle>
                          <FaVideo style={{ marginRight: '10px' }} />
                          Recording {index + 1}
                        </RecordingTitle>
                        <RecordingDetails>
                          <RecordingDetail>Duration: {formatDuration(recording.duration)}</RecordingDetail>
                          <RecordingDetail>Created: {new Date(recording.createdAt).toLocaleString()}</RecordingDetail>
                        </RecordingDetails>
                        <RecordingActions>
                          <DownloadButton as="a" href={`${SERVER_URL.replace('/api', '')}${recording.fileUrl}`} target="_blank" download>
                            <FaFileDownload style={{ marginRight: '5px' }} /> Download Recording
                          </DownloadButton>
                          <ViewButton as="a" href={`${SERVER_URL.replace('/api', '')}${recording.fileUrl}`} target="_blank">
                            Play Recording
                          </ViewButton>
                        </RecordingActions>
                      </RecordingItem>
                    ))}
                  </TranscriptContainer>
                </>
              ) : (
                <NoTranscript>
                  No recordings available for this meeting.
                </NoTranscript>
              )}
            </ModalContent>
          </TranscriptModal>
        </ModalOverlay>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h1 {
    font-size: 24px;
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    
    h1 {
      margin-bottom: 10px;
    }
  }
`;

const StatusCountsContainer = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const StatusCount = styled.div`
  background-color: #f0f0f0;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
`;

const FilterTabs = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }
`;

const FilterTab = styled.button`
  padding: 10px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  position: relative;
  white-space: nowrap;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: ${props => props.active ? '#3182ce' : 'transparent'};
    transition: background-color 0.3s;
  }
  
  color: ${props => props.active ? '#3182ce' : '#4a5568'};
  font-weight: ${props => props.active ? '600' : '400'};
`;

const MeetingsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MeetingCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const MeetingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
`;

const MeetingTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70%;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  
  background-color: ${props => {
    switch(props.status) {
      case 'connected': return '#e3f2fd';
      case 'in_progress': return '#fff8e1';
      case 'ongoing': return '#e8f5e9';
      case 'completed': return '#f5f5f5';
      default: return '#f0f0f0';
    }
  }};
  
  color: ${props => {
    switch(props.status) {
      case 'connected': return '#1976d2';
      case 'in_progress': return '#f57c00';
      case 'ongoing': return '#388e3c';
      case 'completed': return '#616161';
      default: return '#757575';
    }
  }};
`;

const MeetingDetails = styled.div`
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
`;

const DetailItem = styled.div`
  display: flex;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  width: 100px;
  color: #4a5568;
`;

const DetailValue = styled.span`
  flex: 1;
`;

const MeetingInteractions = styled.div`
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  gap: 20px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const InteractionSection = styled.div`
  flex: 1;
`;

const InteractionHeader = styled.div`
  margin-bottom: 10px;
  
  h4 {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    color: #4a5568;
  }
`;

const CommentsList = styled.div`
  max-height: 150px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }
`;

const CommentItem = styled.div`
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const CommentAuthor = styled.div`
  font-weight: 500;
  font-size: 13px;
`;

const CommentText = styled.div`
  font-size: 13px;
  margin: 4px 0;
`;

const CommentTime = styled.div`
  font-size: 11px;
  color: #718096;
`;

const LikesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const LikeItem = styled.span`
  background-color: #f0f0f0;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
`;

const MoreLikes = styled.span`
  font-size: 12px;
  color: #718096;
  padding: 2px 0;
`;

const NoInteractions = styled.div`
  font-size: 13px;
  color: #718096;
  font-style: italic;
`;

const MeetingActions = styled.div`
  padding: 15px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  background-color: ${props => props.secondary ? '#f0f0f0' : '#3182ce'};
  color: ${props => props.secondary ? '#4a5568' : 'white'};
  border: none;
  
  &:hover {
    background-color: ${props => props.secondary ? '#e0e0e0' : '#2c5282'};
  }
`;

const NoMeetings = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #718096;
  font-size: 16px;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #718096;
  font-size: 16px;
`;

// Add these styled component definitions before the export default statement

// Add new styled components for display mode tabs
const DisplayModeTabs = styled.div`
  display: flex;
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const DisplayModeTab = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--text-color)'};
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const TranscriptModal = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
    color: #333;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const ModalContent = styled.div`
  padding: 20px;
  overflow-y: auto;
  max-height: calc(90vh - 70px);
`;

const TranscriptContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 10px;
`;

const TranscriptSegment = styled.div`
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const TranscriptTime = styled.div`
  font-size: 12px;
  color: #718096;
  margin-bottom: 4px;
`;

const TranscriptSpeaker = styled.span`
  font-weight: 600;
  margin-right: 8px;
`;

const TranscriptText = styled.span`
  font-size: 14px;
`;

const NoTranscript = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #718096;
  font-size: 16px;
`;

const TranscriptActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 15px;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background-color: #3182ce;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #2c5282;
  }
`;

// Add these styled components at the end of the file
const RecordingItem = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background-color: var(--bg-secondary);
  border-radius: 8px;
`;

const RecordingTitle = styled.h4`
  display: flex;
  align-items: center;
  margin: 0 0 10px 0;
  color: var(--text-primary);
`;

const RecordingDetails = styled.div`
  margin-bottom: 10px;
`;

const RecordingDetail = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 5px;
`;

const RecordingActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ViewButton = styled(DownloadButton)`
  background-color: var(--primary);
  &:hover {
    background-color: var(--primary-dark);
  }
`;

export default MeetingHistory;